import {
  DateField,
  List,
  NumberField,
  SelectInput,
  SearchInput,
  DateInput,
  Show,
  SimpleShowLayout,
  TextField,
  useTranslate,
  useDataProvider,
  FunctionField,
  ReferenceField,
  RaRecord,
  useGetOne,
  useRecordContext,
  SelectField,
  Identifier,
  FieldProps
} from 'react-admin';
import { japaneseMessages } from '../i18n/ja';
import { getLocalStorageId } from '../authProvider';
import EnumTextField from '../common_modules/components/EnumTextField';
import FlexibleListLayout from '../common_modules/components/FlexibleListLayout';
import { format, toZonedTime } from 'date-fns-tz';
import jsonExport from 'jsonexport/dist';
import React, { FC, useMemo, useCallback, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { Typography, Card, CardContent, Drawer } from '@mui/material';
import { ApproveButton, DisapproveButton } from '../common_modules/components/order/OrderRowActions';
import { AutoApprovalControl } from '../common_modules/components/order/AutoApprovalControl';
import { ORDER_DISAPPROVAL_REASON_CODE, COUPON_FLAG } from '../common_modules/constants/choices';
import YenField from '../common_modules/components/YenField';
import downloadCSVCompat from '../common_modules/utils/downloadCSVCompat';
import { MAX_PER_PAGE } from '../common_modules/constants/define';
import OrderDetailExpand from '../common_modules/components/OrderDetailExpand';
import ExistingCustomerField, { formatExistingCustomerForCsv } from '../common_modules/components/ExistingCustomerField';
import { Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OrderListActions from '../common_modules/components/order/OrderListActions';

const jstTimeZone = 'Asia/Tokyo'; // JSTタイムゾーンを定義
interface OrderDetail {
  id: string | number;
  userPurchaseId: string;
  productId: string;
  productName: string;
  productPrice: number;
  quantity: number;
  reward?: number;
  rewardPrice?: number;
}
const orderApprovalStatus = Object.entries(japaneseMessages.values.orderApprovalStatus).map(([id, name]) => ({
  id: Number(id),
  name
}));

const parseCouponValue = (value: number): boolean | undefined => {
  if (value === 1) return true;
  if (value === 2) return false;
  return undefined;
};

const formatCouponValue = (value: boolean | undefined) => {
  if (value === true) return 1;
  if (value === false) return 2;
  return 0;
};

const DisapprovalReasonDrawer = ({ record }: { record: RaRecord | null }) => {
  const translate = useTranslate();

  if (!record || record.approvalStatus !== 2) {
    return null;
  }

  return (
    <Card sx={{ width: '300px' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {translate('resources.orders.fields.disapprovalReasonCode')}
        </Typography>
        <SimpleShowLayout record={record} sx={{ border: 0 }}>
          <SelectField source="disapprovalReasonCode" choices={ORDER_DISAPPROVAL_REASON_CODE} />
          <TextField source="disapprovalReason" multiline />
        </SimpleShowLayout>
      </CardContent>
    </Card>
  );
};

/**
 * lastStatusChangerTypeの値に応じて表示を切り替えるカスタムフィールド
 */
const LastStatusChangerField: FC<FieldProps> = () => {
  const record = useRecordContext();

  if (!record || record.lastStatusChangerType == null) {
    return <span>-</span>;
  }

  if (record.lastStatusChangerType === 1) {
    return (
      <Typography component="span" variant="body2">
        successfee
      </Typography>
    );
  }

  if (record.lastStatusChangerType === 2) {
    if (record.lastStatusChangerId == null || record.lastStatusChangerId === 0) {
      if (record.approvalStatus === 2) {
        return <Typography>自動否認</Typography>;
      } else if (record.approvalStatus === 1) {
        return <Typography>自動承認</Typography>;
      }
    }
    return (
      <ReferenceField
        source="lastStatusChangerId"
        reference="references/accounts"
        record={record}
        link={(record: any) => `/accounts/${record.id}/show`}
      >
        <TextField source="name" />
      </ReferenceField>
    );
  }

  return <span>-</span>;
};

const RemainingPeriodField = () => {
  const record = useRecordContext(); // 「保留中」でない、またはデータがない場合は何も表示しない

  if (!record || record.approvalStatus !== 0 || record.remainingPeriod == null) {
    return null;
  }

  // 期限切れ（0ミリ秒以下）の場合は「期限切れ」と表示して終了
  if (record.remainingPeriod <= 0) {
    return (
      <Typography component="span" variant="body2" sx={{ color: 'red' }}>
        期限切れ
      </Typography>
    );
  } // 残り時間を「日」「時間」「分」単位で計算（小数点以下切り上げ）

  const remainingDays = Math.ceil(record.remainingPeriod / (1000 * 60 * 60 * 24));
  const remainingHours = Math.ceil(record.remainingPeriod / (1000 * 60 * 60));
  const remainingMinutes = Math.ceil(record.remainingPeriod / (1000 * 60));

  // ★★★ 表示するテキストを決定 ★★★
  let displayText = '';
  if (remainingDays > 1) {
    // 1日より多い場合 → あと〇日
    displayText = `あと${remainingDays}日`;
  } else if (remainingHours > 1) {
    // 1時間より多い場合 → あと〇時間
    displayText = `あと${remainingHours}時間`;
  } else {
    // 1時間以下の場合 → あと〇分
    displayText = `あと${remainingMinutes}分`;
  } //  3日以下の場合は赤字にする

  const textColor = remainingDays <= 3 ? 'red' : 'inherit';

  return (
    <Typography component="span" variant="body2" sx={{ color: textColor }}>
      {displayText}
    </Typography>
  );
};

export const OrderList = () => {
  const translate = useTranslate();
  const queryClient = useQueryClient();
  const [selectedOrder, setSelectedOrder] = useState<RaRecord | null>(null);
  const dataProvider = useDataProvider();

  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const filterDefaultValues = useMemo(() => {
    const today = new Date();
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(today.getDate() - 7); // フィルターの初期値を定義
    return {
      orderDate_start: formatDate(oneWeekAgo),
      orderDate_end: formatDate(today)
    };
  }, []);

  const clientId = getLocalStorageId();
  const {
    data: settings,
    isLoading: isLoadingSettings,
    error: settingsError
  } = useGetOne('settings', { id: clientId }, { enabled: !!clientId });

  const isApproveEnabled = settings && (settings.autoOrderApprovalFlag & 1) > 0;

  useEffect(() => {
    queryClient.setQueryDefaults('orders', {
      refetchOnWindowFocus: false
    });
  }, [queryClient]);

  const createExporter = useCallback((type: 'order' | 'detail') => {
    return async (records: any[]) => {
      if (!records || records.length === 0) return;

      const isDetailExport = type === 'detail';

      // typeに応じて設定を切り替え
      const filename = isDetailExport ? '注文詳細' : '注文ID別';
      const exportKeys = isDetailExport
        ? [
            'orderId',
            'ecSiteClientId',
            'fuid',
            'usageCount',
            'isExistingCustomer',
            'partnerName',
            'affiliateId',
            'orderDate',
            'hasCoupon',
            'isCancelled',
            'approvalStatus',
            'disapprovalReasonCode',
            'disapprovalReason',
            'approvedAt',
            'lastStatusChangedAt',
            'lastStatusChanger',
            'productId',
            'productName',
            'quantity',
            'productSubtotal',
            'isExclude',
            'basePartnerReward',
            'campaignRewardDelta',
            'usageCountRewardDelta',
            'existingCustomerRewardDelta',
            'productReward',
            'productRewardPrice'
          ]
        : [
            'orderId',
            'ecSiteClientId',
            'fuid',
            'usageCount',
            'isExistingCustomer',
            'partnerName',
            'affiliateId',
            'orderDate',
            'price',
            'reward',
            'hasCoupon',
            'isCancelled',
            'approvalStatus',
            'disapprovalReasonCode',
            'disapprovalReason',
            'approvedAt',
            'lastStatusChangedAt',
            'lastStatusChanger'
          ];

      // --- 1. 関連データのIDを収集 ---
      const partnerIds = [...new Set(records.map((record) => record.partnerId).filter(Boolean))];
      const accountChangerIds = [
        ...new Set(
          records
            .filter((r) => r.lastStatusChangerType === 2 && r.lastStatusChangerId)
            .map((r) => r.lastStatusChangerId)
        )
      ];
      const orderIds = isDetailExport ? records.map((r) => r.id) : [];

      // --- 2. 関連データを並行して一括取得 ---
      const [partnersData, accountsData, orderDetailsData] = await Promise.all([
        partnerIds.length > 0
          ? dataProvider.getMany('references/partners', { ids: partnerIds })
          : Promise.resolve({ data: [] }),
        accountChangerIds.length > 0
          ? dataProvider.getMany('references/accounts', { ids: accountChangerIds })
          : Promise.resolve({ data: [] }),
        orderIds.length > 0
          ? dataProvider.getManyDetails('orders/details/bulk', { ids: orderIds })
          : Promise.resolve({ data: [] })
      ]);

      // --- 3. 取得したデータをIDをキーにしたマップに変換 ---
      const partnerMap = new Map(partnersData.data.map((p) => [p.id, p.name]));
      const accountMap = new Map(accountsData.data.map((a) => [a.id, a.name]));
      const orderDetailsMap = new Map<Identifier, OrderDetail[]>();
      if (isDetailExport) {
        orderDetailsData.data.forEach((detail: OrderDetail) => {
          const orderId = detail.userPurchaseId;
          if (!orderDetailsMap.has(orderId)) {
            orderDetailsMap.set(orderId, []);
          }
          orderDetailsMap.get(orderId)?.push(detail);
        });
      }

      // --- 4. CSVデータに変換 ---
      const dataToExport = records.flatMap((record) => {
        const commonData: Record<string, any> = {};
        const commonKeys = exportKeys.filter(
          (k) =>
            ![
              'productId',
              'productName',
              'quantity',
              'productSubtotal',
              'isExclude',
              'basePartnerReward',
              'campaignRewardDelta',
              'usageCountRewardDelta',
              'existingCustomerRewardDelta',
              'productReward',
              'productRewardPrice'
            ].includes(k)
        );

        commonKeys.forEach((key) => {
          let value = record[key];
          switch (key) {
            case 'ecSiteClientId':
              value = record.ecSiteClientId?.toString() ?? '';
              break;
            case 'partnerName':
              value = partnerMap.get(record.partnerId) || '-';
              break;
            case 'orderDate':
            case 'lastStatusChangedAt':
            case 'approvedAt':
              if (value)
                try {
                  value = format(toZonedTime(new Date(value), jstTimeZone), 'yyyy/MM/dd HH:mm:ss', {
                    timeZone: jstTimeZone
                  });
                } catch (e) {
                  /* no-op */
                }
              break;
            case 'price':
              value = value != null ? `${value.toLocaleString()}` : '';
              break;
            case 'reward':
              if (value != null) {
                const totalReward = record.introducerRewardPrice != null
                  ? Number(record.introducerRewardPrice) + Number(record.reward)
                  : Number(record.reward);
                value = `${totalReward.toLocaleString()}`;
              } else {
                value = '';
              }
              break;
            case 'hasCoupon':
              value = value ? '〇' : '✕';
              break;
            case 'isCancelled':
              value = value ? '〇' : '';
              break;
            case 'usageCount':
              value = value == null ? '' : value;
              break;
            case 'isExistingCustomer':
              value = formatExistingCustomerForCsv(value);
              break;
            case 'isExclude':
              value = value ? '〇' : '✕';
              break;
            case 'approvalStatus':
              value = translate(`values.orderApprovalStatus.${value}`, { _: value });
              break;
            case 'disapprovalReasonCode':
              if (value) {
                const choice = ORDER_DISAPPROVAL_REASON_CODE.find((c) => c.id === value);
                value = choice ? choice.name : value;
              }
              break;
            case 'lastStatusChanger':
              if (record.lastStatusChangerType === 1) {
                value = 'successfee';
              } else if (record.lastStatusChangerType === 2) {
                if (record.lastStatusChangerId == null || record.lastStatusChangerId === 0) {
                  if (record.approvalStatus === 2) value = '自動否認';
                  else if (record.approvalStatus === 1) value = '自動承認';
                } else {
                  value = accountMap.get(record.lastStatusChangerId) || `(ID: ${record.lastStatusChangerId})`;
                }
              }
              break;
          }
          commonData[translate(`resources.orders.fields.${key}`, { _: key })] = value !== undefined ? value : '';
        });

        if (!isDetailExport) {
          return [commonData];
        } else {
          const details = orderDetailsMap.get(record.id) || [];
          if (details.length === 0) {
            const row = { ...commonData };
            row[translate('resources.orders.fields.productName', { _: 'productName' })] = '商品明細なし';
            return [row];
          }
          return details.flatMap((detail: any) => {
            const isFirstTier = !detail.partnerRewardRatio;
            
            const displayCampaignDelta = isFirstTier ? detail.campaignRewardDelta : detail.childCampaignRewardDelta;
            const displayUsageCountDelta = isFirstTier ? detail.usageCountRewardDelta : detail.childUsageCountRewardDelta;
            const displayExistingCustomerDelta = isFirstTier ? detail.existingCustomerRewardDelta : detail.childExistingCustomerRewardDelta;

            const childRow = {
              ...commonData,
              [translate('resources.orders.fields.productId', { _: 'productId' })]: detail.productId,
              [translate('resources.orders.fields.productName', { _: 'productName' })]: detail.productName,
              [translate('resources.orders.fields.quantity', { _: 'quantity' })]: detail.quantity,
              [translate('resources.orders.fields.productSubtotal', { _: 'productSubtotal' })]:
                detail.productPrice.toLocaleString(),
              [translate('resources.orders.fields.isExclude', { _: 'isExclude' })]: detail.isExclude ? '〇' : '✕',
              [translate('resources.orders.fields.basePartnerReward')]: detail.basePartnerReward ?? '',
              [translate('resources.orders.fields.campaignRewardDelta')]: displayCampaignDelta ?? '',
              [translate('resources.orders.fields.usageCountRewardDelta')]: displayUsageCountDelta ?? '',
              [translate('resources.orders.fields.existingCustomerRewardDelta')]:
                displayExistingCustomerDelta === "-9999" ? "-" : (displayExistingCustomerDelta ?? ''),
              [translate('resources.orders.fields.productReward')]:
                detail.productName === '割引' ? '' : detail.reward != null ? detail.reward.toLocaleString() + '%' : '',
              [translate('resources.orders.fields.productRewardPrice')]:
                detail.rewardPrice != null ? Math.floor(parseFloat(detail.rewardPrice)).toLocaleString() : '',
            };

            const introducerPrice = detail.introducerRewardPrice !== null && detail.introducerRewardPrice !== undefined ? parseFloat(detail.introducerRewardPrice) : 0;
            const isBaseIntroducerRewardNull = detail.baseIntroducerReward === null || detail.baseIntroducerReward === undefined || detail.baseIntroducerReward === '';

            const hasIntroducer =
              detail.introducerRewardRatio !== null &&
              detail.introducerRewardRatio !== undefined &&
              detail.introducerRewardRatio !== '' &&
              !(introducerPrice === 0 && isBaseIntroducerRewardNull);

            if (hasIntroducer) {

              const parentRow = {
                ...commonData,
                [translate('resources.orders.fields.productId', { _: 'productId' })]: detail.productId,
                [translate('resources.orders.fields.productName', { _: 'productName' })]: detail.productName,
                [translate('resources.orders.fields.quantity', { _: 'quantity' })]: detail.quantity,
                [translate('resources.orders.fields.productSubtotal', { _: 'productSubtotal' })]:
                  detail.productPrice.toLocaleString(),
                [translate('resources.orders.fields.isExclude', { _: 'isExclude' })]: detail.isExclude ? '〇' : '✕',
                [translate('resources.orders.fields.basePartnerReward')]: detail.baseIntroducerReward ?? '',
                [translate('resources.orders.fields.campaignRewardDelta')]: detail.parentCampaignRewardDelta ?? '',
                [translate('resources.orders.fields.usageCountRewardDelta')]: detail.parentUsageCountRewardDelta ?? '',
                [translate('resources.orders.fields.existingCustomerRewardDelta')]:
                  detail.parentExistingCustomerRewardDelta === "-9999" ? "-" : (detail.parentExistingCustomerRewardDelta ?? ''),
                [translate('resources.orders.fields.productReward')]:
                  detail.productName === '割引' ? '' : detail.introducerReward != null ? parseFloat(detail.introducerReward).toLocaleString() + '%' : '',
                [translate('resources.orders.fields.productRewardPrice')]:
                  detail.introducerRewardPrice != null ? Math.floor(parseFloat(detail.introducerRewardPrice)).toLocaleString() : '',
              };
              return [childRow, parentRow];
            }

            return [childRow];
          });
        }
      });

      // --- 5. CSVをダウンロード ---
      const translatedHeaders = exportKeys.map((key) => translate(`resources.orders.fields.${key}`, { _: key }));
      jsonExport(dataToExport, { headers: translatedHeaders }, (err, csv) => {
        if (err) return console.error(err);
        downloadCSVCompat('\uFEFF' + csv, filename);
      });
    };
  }, [dataProvider, translate]);

  const handleRowClick = useCallback((id: Identifier, resource: string, record: RaRecord): false => {
    // 承認ステータスが「否認」(2) の場合のみDrawerを開く
    if (record.approvalStatus === 2) {
      setSelectedOrder(prev => prev && prev.id === id ? null : record);
    } else {
      setSelectedOrder(null); // 「否認」以外はDrawerを閉じる
    }
    return false; // デフォルトの画面遷移を無効化
  }, []);

  return (
    <>
      <List
        filterDefaultValues={filterDefaultValues}
        actions={<OrderListActions createExporter={createExporter} />}
        filters={[
          <DateInput
            key="orderDate_start"
            source="orderDate_start"
            label={translate('resources.orders.fields.orderDate_start')}
            alwaysOn
          />,
          <DateInput
            key="orderDate_end"
            source="orderDate_end"
            label={translate('resources.orders.fields.orderDate_end')}
            alwaysOn
          />,
          <SearchInput
            key="orderId"
            source="orderId"
            placeholder={translate('resources.orders.fields.orderId')}
            alwaysOn
          />,
          <SearchInput
            key="affiliateId"
            source="affiliateId"
            placeholder={translate('resources.orders.fields.affiliateId')}
            defaultValue=""
          />,
          <SearchInput
            key="ecSiteClientId"
            source="ecSiteClientId"
            placeholder={translate('resources.orders.fields.ecSiteClientId')}
          />,
          <SelectInput
            key="hasCoupon"
            source="hasCoupon"
            label="クーポン"
            choices={COUPON_FLAG}
            alwaysOn
            parse={parseCouponValue}
            format={formatCouponValue}
            emptyText="全て"
            emptyValue={0}
          />,
          <SelectInput
            key="approvalStatus"
            source="approvalStatus"
            label={translate('resources.orders.fields.approvalStatus')}
            choices={orderApprovalStatus}
            emptyText="(選択解除)"
            alwaysOn
          />
        ]}
        sort={{ field: 'orderDate', order: 'DESC' }}
      >
        <AutoApprovalControl isLoading={isLoadingSettings} error={settingsError} settings={settings} />
        <FlexibleListLayout
          rowClick={false}
          rowSx={(record: RaRecord) => ({
            ...(selectedOrder &&
              selectedOrder.id === record.id && {
                backgroundColor: 'action.selected'
              })
          })}
          // bulkActionButtons={(() => {
          //   const { selectedIds = [] } = useListContext() || {};
          //   return <BulkOrderActionButtons selectedIds={selectedIds} />;
          // })()}
          bulkActionButtons={false}
          isRowSelectable={(record: RaRecord) => record && record.approvalStatus === 0}
          expand={<OrderDetailExpand />}
          sx={{
            // 'fixed' を指定し、テーブル幅を100%に固定
            tableLayout: 'fixed',
            width: '100%',

            // ヘッダーセル (<th>)
            '& .RaDatagrid-headerCell': {
              whiteSpace: 'nowrap',
              padding: '12px 8px'
            },

            // データセル (<td>)
            '& .RaDatagrid-cell': {
              whiteSpace: 'normal !important',
              wordBreak: 'break-word',
              padding: '12px 8px'
            },

            // ECサイト顧客ID の右余白を消す
            '& .client-id-column': {
              width: '120px',
              paddingRight: '0px !important',
              textAlign: 'center'
            },

            // ツールチップ の左余白を消す
            '& .tooltip-column': {
              width: '24px',
              paddingLeft: '0px !important'
            }
          }}
        >
          <FunctionField
            label={translate('resources.orders.fields.approvalStatus')}
            sortBy="approvalStatus"
            render={(record: RaRecord) => {
              if (!record) return null;

              const status = record.approvalStatus;
              const text = translate(`values.orderApprovalStatus.${status}`, { _: status });

              let style: React.CSSProperties = {};

              switch (status) {
                case 1: // 承認済み
                  style.color = '#419867';
                  break;
                case 2: // 否認
                  style.color = '#d95c4b';
                  style.cursor = 'pointer';
                  style.textDecoration = 'underline';
                  break;
              }

              const handleClick = () => {
                if (status === 2) {
                  handleRowClick(record.id, 'orders', record);
                }
              };

              return (
                <span style={style} onClick={status === 2 ? handleClick : undefined}>
                  {text}
                </span>
              );
            }}
          />
          {isApproveEnabled && <RemainingPeriodField />}
          <FunctionField
            render={(record: RaRecord) =>
              record && record.approvalStatus === 0 ? (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <ApproveButton />
                  <DisapproveButton />
                </div>
              ) : null
            }
          />
          <TextField source="orderId" label={translate('resources.orders.fields.orderId')} />
          <FunctionField
            source="ecSiteClientId"
            sortBy="ecSiteClientId"
            label={translate('resources.orders.fields.ecSiteClientId')}
            className="client-id-column"
            headerClassName="client-id-column"
            render={(record: any) => {
              const value = record.ecSiteClientId;
              if (!value) {
                return <div style={{ width: '100%', textAlign: 'center' }}>-</div>;
              }
              return value;
            }}
          />
          {/* ツールチップ表示用の列（ソート機能なし・データ表示なし） */}
          <FunctionField
            label={
              <Tooltip
                title="※購入者ID（会員ID）の提供がない場合やIDに個人情報が含まれる場合は、「ECサイト顧客ID」は「-」と表示されます。"
                placement="top"
                arrow
              >
                <div style={{ display: 'flex', alignItems: 'center', cursor: 'help' }}>
                  <InfoOutlinedIcon fontSize="small" color="error" />
                </div>
              </Tooltip>
            }
            sortable={false}
            render={() => null}
            headerClassName="tooltip-column"
            className="tooltip-column"
          />
          <TextField source="fuid" label={translate('resources.orders.fields.fuid')} />
          <FunctionField
            source="usageCount"
            label={translate('resources.orders.fields.usageCount')}
            render={(record: any) => (record?.usageCount == null ? '' : record.usageCount)}
          />
          <ExistingCustomerField
            source="isExistingCustomer"
            sortBy="isExistingCustomer"
            label={translate('resources.orders.fields.isExistingCustomer')}
          />
          <FunctionField
            label={translate('resources.orders.fields.partnerName')}
            sortable={false}
            render={(record: RaRecord) => {
              if (!record || !record.partnerId) return null;
              const link = `/affiliates/${record.affiliateId}/show`;
              return (
                <ReferenceField record={record} source="partnerId" reference="references/partners" link={link}>
                  <TextField source="name" />
                </ReferenceField>
              );
            }}
          />
          <TextField source="affiliateId" label={translate('resources.orders.fields.affiliateId')} />
          <DateField showTime={true} source="orderDate" />
          <YenField source="price" />
          <FunctionField
            source="reward"
            label={translate('resources.orders.fields.reward')}
            render={(record: any) => {
              if (!record) return '';
              const reward = record.introducerRewardPrice != null
                ? record.introducerRewardPrice + record.reward
                : record.reward;
              return `${reward.toLocaleString()} 円`;
            }}
          />
          <EnumTextField source="hasCoupon" translationKeyPrefix="values.hasCoupon" />
          <EnumTextField source="isCancelled" translationKeyPrefix="values.isCancelled" />
          <DateField
            sortable={false}
            source="lastStatusChangedAt"
            label={translate('resources.orders.fields.lastStatusChangedAt')}
            showTime
          />
          <LastStatusChangerField label={translate('resources.orders.fields.lastStatusChanger')} />
        </FlexibleListLayout>
      </List>
      <Drawer anchor="right" open={selectedOrder !== null} onClose={() => setSelectedOrder(null)}>
        <DisapprovalReasonDrawer record={selectedOrder} />
      </Drawer>
    </>
  );
};

export const OrderShow = () => (
  <Show>
    <SimpleShowLayout>
      <TextField source="orderId" />
      <TextField source="ecSiteClientId" />
      <TextField source="affiliateId" />
      <DateField showTime={true} source="orderDate" />
      <NumberField source="price" />
      <EnumTextField source="isCancelled" translationKeyPrefix="values.isCancelled" />
    </SimpleShowLayout>
  </Show>
);

